import {
  Textarea,
  Image as MantineImage,
  FileButton,
  Button,
  Group,
  Paper,
  Stack,
  Text,
  ActionIcon,
  Grid,
  UnstyledButton,
  useMantineTheme,
} from '@mantine/core';
import { IconX } from '@tabler/icons-react';
import { useState } from 'react';

import { KeywordRuleForm } from '../../../hooks/useKeywordRuleForm';
import { ChunkPlatform, getChunkUsage } from '../../../utils/chunk-limits';
import { AddButton } from '../../AddButton';
import { RemoveButton } from '../../RemoveButton';

import { prophetTypes } from '@/features/prophet';
import { COLORS } from '@/utils/colors';

// See Rules/RuleForm/MessagesFields.tsx for the full data-shape recap.
// TL;DR: outer array = mutually-exclusive VARIATIONS (Prophet picks one at
// random); inner array = sequential parts of the chosen variation
// (Prophet delivers all in order, one rate-limit slot each). Both
// keyword-rule message categories are multi-message bursts, so both get
// "Вариант N" outside + "Часть N" inside labels.
const CHUNK_CATEGORIES: Set<keyof prophetTypes.KeywordRule['messages']> = new Set([
  'spreads',
  'repeatedKeyword',
]);

type MessageProps = {
  message: prophetTypes.RuleMessage;
  placeholder: string;
  form: KeywordRuleForm;
  index: number;
  path: string;
  platform: ChunkPlatform;
  partLabel?: string;
};

export const Message = ({
  message,
  placeholder,
  form,
  index,
  path,
  platform,
  partLabel,
}: MessageProps) => {
  const [isOpen, setIsOpen] = useState(false);

  if (message.type === 'image') {
    const source =
      'url' in message ? message.url : message.file ? URL.createObjectURL(message.file) : undefined;
    return (
      <Stack gap={4}>
        {partLabel && (
          <Text size="xs" opacity={0.5}>
            {partLabel} (изображение)
          </Text>
        )}
        <UnstyledButton w="100%" onClick={() => setIsOpen((v) => !v)}>
          <MantineImage
            height={isOpen ? undefined : 100}
            radius="sm"
            fit={isOpen ? 'contain' : 'cover'}
            src={source}
          />
        </UnstyledButton>
      </Stack>
    );
  }

  if (message.type === 'text') {
    // Same platform-aware chunk gate as the standard Rules form. See
    // Rules/RuleForm/MessagesFields.tsx and utils/chunk-limits.ts.
    const value = (message.text ?? '') as string;
    const usage = getChunkUsage(value, platform);
    const unitLabel = 'символов';
    return (
      <Stack gap={4}>
        {partLabel && (
          <Text size="xs" opacity={0.5}>
            {partLabel}
          </Text>
        )}
        <Textarea
          placeholder={placeholder}
          autosize
          minRows={2}
          error={usage.exceedsLimit ? `Максимум ${usage.limit} ${unitLabel}` : undefined}
          {...form.getInputProps(`${path}.${index}.text`)}
        />
        <Text size="xs" c={usage.exceedsLimit ? 'red' : 'dimmed'} ta="right">
          {usage.current} / {usage.limit} {unitLabel}
        </Text>
      </Stack>
    );
  }

  return null;
};

type MessagesProps = {
  form: KeywordRuleForm;
  messages: prophetTypes.RuleMessage[];
  placeholder: string;
  name: keyof prophetTypes.KeywordRule['messages'];
  index: number;
  platform: ChunkPlatform;
};

export const Messages = ({ messages, placeholder, form, name, index, platform }: MessagesProps) => {
  const path = `messages.${name}.${index}`;
  const showPartLabels = CHUNK_CATEGORIES.has(name);
  const addTextLabel = showPartLabels ? 'Добавить часть' : 'Text';

  const onFileSelect = (file: File | null) => {
    if (file) {
      form.insertListItem(path, { type: 'image', file });
    }
  };

  return (
    <>
      {messages.map((message, i) => (
        <Grid key={i} align="center">
          <Grid.Col span={10}>
            <Message
              message={message}
              placeholder={placeholder}
              form={form}
              index={i}
              path={path}
              platform={platform}
              partLabel={showPartLabels ? `Часть ${i + 1}` : undefined}
            />
          </Grid.Col>
          <Grid.Col span={2}>
            <RemoveButton onClick={() => form.removeListItem(path, i)} />
          </Grid.Col>
        </Grid>
      ))}
      {!(messages.length >= 1) && (
        <Group justify="center">
          <Button.Group>
            <Button
              variant="default"
              size="compact-sm"
              onClick={() => form.insertListItem(path, { type: 'text', text: '' })}
            >
              {addTextLabel}
            </Button>
            <FileButton accept="image/png,image/jpeg" onChange={onFileSelect}>
              {(props) => (
                <Button
                  style={{
                    borderTopRightRadius: 'var(--mantine-radius-sm)',
                    borderBottomRightRadius: 'var(--mantine-radius-sm)',
                  }}
                  variant="default"
                  size="compact-sm"
                  {...props}
                >
                  Image
                </Button>
              )}
            </FileButton>
          </Button.Group>
        </Group>
      )}
    </>
  );
};

type Props = {
  form: KeywordRuleForm;
  name: keyof prophetTypes.KeywordRule['messages'];
  placeholder: string;
  isRequired?: boolean;
  platform: ChunkPlatform;
};

// Outer card is a mutually-exclusive VARIATION. Both keyword-rule
// categories are multi-message bursts, so label as "Вариант N".
const VARIATION_LABEL: Partial<Record<keyof prophetTypes.KeywordRule['messages'], string>> = {
  spreads: 'Вариант',
  repeatedKeyword: 'Вариант',
};

export const MessagesFields = ({ form, name, placeholder, platform }: Props) => {
  const theme = useMantineTheme();
  const fields = form.values.messages[name];
  const path = `messages.${name}`;
  const variationLabel = VARIATION_LABEL[name] ?? '#';
  return (
    <>
      {fields?.map((messages, index) => {
        return (
          <Paper key={index} my="sm" radius="sm" bg={COLORS.accentBackground} p="md">
            <Group justify="space-between" mb="xs">
              <Text size="sm" opacity={0.6}>
                {variationLabel} {index + 1}
              </Text>
              <ActionIcon
                color={theme.colors.dark[0]}
                variant="transparent"
                onClick={() => form.removeListItem(path, index)}
              >
                <IconX opacity={0.6} size={16} />
              </ActionIcon>
            </Group>
            <Stack>
              <Messages
                name={name}
                messages={messages}
                placeholder={placeholder}
                form={form}
                index={index}
                platform={platform}
              />
            </Stack>
          </Paper>
        );
      })}
      <AddButton size={25} onClick={() => form.insertListItem(path, [])} />
    </>
  );
};
